"use client";

import React, { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { useSnapshot } from "valtio";
import {
  SetupChecklistStore,
  fetchChecklist,
  closeSheet,
  refreshChecklistIfStale,
  syncPendingCelebrationDismiss,
} from "@/src/store/modules/setupChecklist";
import {
  hasPendingPushSettingsReturn,
  readSystemNotificationEnabled,
  resolvePendingPushSettingsReturn,
} from "../utils/pushSettingsReturn";
import { openSetupChecklistSheetModal } from "./SetupChecklistSheet";
import { openSetupCompleteCelebrationModal } from "./SetupCompleteCelebrationModal";
import { getCelebrationDismissKey } from "../constants/stepMeta";
import { rootStore } from "@/src/store/rootStore";

/**
 * 全局起步清单监听管理器
 * 挂载于 RootLayoutClient，监听 isSheetOpen 与 isCelebrationModalOpen 并呼出对应弹层
 */
export const SetupChecklistGlobalManager: React.FC = () => {
  const snap = useSnapshot(SetupChecklistStore);
  const userSnap = useSnapshot(rootStore.user);
  const userId = userSnap.ownerInfo?.id || "";

  const isSheetOpenRef = useRef(false);
  const isCelebrationOpenRef = useRef(false);

  // App 启动时预拉取一次清单数据
  useEffect(() => {
    void fetchChecklist();
  }, []);

  // 完成财务健康分 / 建 tracker / 激活 goal 后清单要跟上：页面切回前台、bfcache 恢复、路由切换时被动刷新
  // （内部有请求去重与 10s 节流，6/6 时靠这里拉到 showCelebration 弹出庆祝页）
  const pathname = usePathname();
  useEffect(() => {
    if (!userId) {
      return;
    }
    void refreshChecklistIfStale();
  }, [userId, pathname]);

  useEffect(() => {
    if (!userId) {
      return;
    }
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        // 从系统通知设置页回到 App：先核对授权结果（S1.6 / S1.7），再走常规被动刷新
        if (hasPendingPushSettingsReturn()) {
          void resolvePendingPushSettingsReturn(readSystemNotificationEnabled);
        }
        void refreshChecklistIfStale();
      }
    };
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        void refreshChecklistIfStale();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pageshow", handlePageShow);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, [userId]);

  // 补发上次失败的庆祝页「已关闭」落库，避免换设备再次弹出
  useEffect(() => {
    if (userId) {
      void syncPendingCelebrationDismiss(userId);
    }
  }, [userId]);

  // 监听 isSheetOpen 并呼出 Sheet Modal
  useEffect(() => {
    if (snap.isSheetOpen && !isSheetOpenRef.current) {
      isSheetOpenRef.current = true;
      openSetupChecklistSheetModal("sheet").finally(() => {
        isSheetOpenRef.current = false;
        closeSheet();
      });
    }
  }, [snap.isSheetOpen]);

  // 监听 isCelebrationModalOpen 并呼出完成庆祝弹窗 (AC-4)
  // 纯 web 的 showCelebration 为前端按可见项重算（后端 6 项口径恒为 false），
  // 防重不能依赖服务端 → 读本地 dismiss key；原生壳下与后端口径双保险
  useEffect(() => {
    if (snap.isCelebrationModalOpen && !isCelebrationOpenRef.current) {
      const dismissedLocally =
        !!userId &&
        localStorage.getItem(getCelebrationDismissKey(userId)) === "true";
      if (dismissedLocally) {
        SetupChecklistStore.isCelebrationModalOpen = false;
        return;
      }
      isCelebrationOpenRef.current = true;
      openSetupCompleteCelebrationModal(userId).finally(() => {
        isCelebrationOpenRef.current = false;
        SetupChecklistStore.isCelebrationModalOpen = false;
      });
    }
  }, [snap.isCelebrationModalOpen, userId]);

  return null;
};
